import type { RouteSettingsView } from '@erp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Button, ErrorBox, PageHeader, Spinner } from '@/components/ui';
import { UserPicker } from '@/components/user-picker';
import { api } from '@/lib/api';
import { routeSettingsQuery } from '@/lib/queries';

/**
 * Настройка маршрутов: кто утверждает, оформляет, оплачивает. Шаги маршрута показаны для справки —
 * они описаны в коде (`packages/shared/src/bpm.ts`).
 */
export function AdminRoutesPage() {
  const q = useQuery(routeSettingsQuery);
  return (
    <div className="max-w-[1080px]">
      <PageHeader
        title="Маршруты согласования"
        description="Назначьте людей на роли. Пока роль не назначена, заявку по маршруту подать нельзя."
      />
      {q.isPending ? (
        <Spinner />
      ) : q.error ? (
        <ErrorBox error={q.error} />
      ) : (
        <div className="flex flex-col gap-6">
          {q.data.map((r) => (
            <RouteCard key={r.code} route={r} />
          ))}
        </div>
      )}
    </div>
  );
}

function RouteCard({ route }: { route: RouteSettingsView }) {
  const qc = useQueryClient();
  const initial = () => Object.fromEntries(route.roles.map((r) => [r.key, r.user ? [r.user.userId] : []]));
  const [values, setValues] = useState<Record<string, string[]>>(initial);
  const [saved, setSaved] = useState(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: сбрасываем форму, когда сервер вернул новые значения
  useEffect(() => setValues(initial()), [route]);

  const dirty = route.roles.some((r) => (values[r.key]?.[0] ?? null) !== (r.user?.userId ?? null));
  const missing = route.roles.filter((r) => !r.user).length;
  const save = useMutation({
    mutationFn: () =>
      api('/bpm/routes/settings', {
        method: 'PUT',
        body: { settings: Object.fromEntries(route.roles.map((r) => [r.key, values[r.key]?.[0] ?? null])) },
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['bpm', 'routes'] });
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2500);
    },
  });

  return (
    <section className="panel" aria-labelledby={`route-${route.code}`}>
      <header className="flex flex-wrap items-baseline justify-between gap-2 border-b border-line px-5 py-3.5">
        <h2 id={`route-${route.code}`} className="text-[16px] font-semibold">
          {route.title}
        </h2>
        {missing > 0 && <span className="text-[13px] font-medium text-danger">Не настроен</span>}
      </header>
      <div className="grid gap-6 px-5 py-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="flex flex-col gap-4">
          {route.roles.map((role) => (
            <div key={role.key}>
              <label htmlFor={`role-${role.key}`} className="field-label">
                {role.label}
              </label>
              <UserPicker
                id={`role-${role.key}`}
                value={values[role.key] ?? []}
                onChange={(v) => setValues((prev) => ({ ...prev, [role.key]: v }))}
                invalid={!values[role.key]?.length}
              />
              <p className="mt-1 text-[12.5px] text-steel">{role.description}</p>
            </div>
          ))}
          <div className="flex items-center gap-3">
            <Button disabled={!dirty} loading={save.isPending} onClick={() => save.mutate()}>
              Сохранить
            </Button>
            {saved && <span className="text-[13px] text-ok">Сохранено</span>}
            {save.error && <span className="text-[13px] text-danger">{save.error.message}</span>}
          </div>
        </div>
        <div>
          <p className="field-label">Шаги маршрута</p>
          <ol className="flex flex-col gap-2">
            {route.steps.map((s, i) => (
              <li key={s.id} className="flex gap-2.5 text-[13.5px]">
                <span className="mt-px inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-sunken text-[11px] font-semibold tabular-nums text-ink-soft">
                  {i + 1}
                </span>
                <span>
                  <span className="font-medium">{s.title}</span>
                  <span className="block text-[12.5px] text-steel">{s.detail}</span>
                </span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}
